import React, { useEffect, useState } from 'react';
import { alertError } from '../../utils/logger';
import { useHistory } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import BusinessIcon from '@material-ui/icons/Business';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import FolderIcon from '@material-ui/icons/Folder';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import StarIcon from '@material-ui/icons/Star';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import { countAdmins } from '../../api/user'
import { getActiveUsers } from '../../api/userCustomer';
import { getAllCustomers } from '../../api/customer';
import { getAllProjects } from '../../api/project';
import { getAllDeals } from '../../api/deal';
import { getAllPlanCustomer } from '../../api/planCustomer';
import { getNonEmpty } from '../../utils/helpers';

export function DashboardPage() {
	const [stats, setStats] = useState({
		admins: 0,
		activeUsers: 0,
		totalCustomers: 0,
		activeCustomers: 0,
		sellers: 0,
		buyers: 0,
		advisors: 0,
		newCustomersThisMonth: 0,
		totalProjects: 0,
		activeProjects: 0,
		pendingProjects: 0,
		highlightedProjects: 0,
		totalDeals: 0,
		activeDeals: 0,
		newDealsThisMonth: 0,
		activeSubscriptions: 0,
		freePlanSubscriptions: 0,
		basicSubscriptions: 0,
		premiumSubscriptions: 0,
	});
	const [loading, setLoading] = useState(true);
	const [recentActivity, setRecentActivity] = useState([]);
	const [customerEvolutionData, setCustomerEvolutionData] = useState({ dates: [], sellers: [], buyers: [], advisors: [] });
	const [evolutionPeriod, setEvolutionPeriod] = useState(30); // days
	const history = useHistory();

	const calculateCustomerEvolution = (sellers, buyers, advisors, days) => {
		const endDate = new Date();
		endDate.setHours(23, 59, 59, 999);
		const startDate = new Date();
		startDate.setDate(startDate.getDate() - days);
		startDate.setHours(0, 0, 0, 0);

		// Initialize data structure
		const dates = [];
		const sellersData = [];
		const buyersData = [];
		const advisorsData = [];

		// Generate date range
		for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
			const dateStr = d.toISOString().split('T')[0];
			dates.push(dateStr);
			sellersData.push(0);
			buyersData.push(0);
			advisorsData.push(0);
		}

		// Count customers created up to each date (cumulative)
		const processCustomers = (customers, dataArray) => {
			customers.forEach(customer => {
				const createdAt = new Date(customer.createdAt);
				if (createdAt <= endDate) {
					const customerDateStr = createdAt.toISOString().split('T')[0];
					// Find all dates from customer creation date onwards
					dates.forEach((dateStr, index) => {
						if (dateStr >= customerDateStr) {
							dataArray[index]++;
						}
					});
				}
			});
		};

		processCustomers(sellers, sellersData);
		processCustomers(buyers, buyersData);
		processCustomers(advisors, advisorsData);

		// Format dates for display (show every Nth date to avoid crowding)
		const dateStep = Math.max(1, Math.floor(dates.length / 15)); // Show max 15 labels
		const formattedDates = dates.map((d, index) => {
			if (index % dateStep === 0 || index === dates.length - 1) {
				return new Date(d).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
			}
			return '';
		});

		setCustomerEvolutionData({
			dates: formattedDates,
			sellers: sellersData,
			buyers: buyersData,
			advisors: advisorsData
		});
	};

	useEffect(() => {
		async function fetchData() {
			setLoading(true);
			try {
				// Administrators
				const admins = await countAdmins();
				
				// Active Users
				const activeUsers = await getActiveUsers();
				const activeUsersCount = activeUsers.data.activeUsers?.length ?? 0;

				// Customers
				const [sellersRes, buyersRes, advisorsRes] = await Promise.all([
					getAllCustomers('seller'),
					getAllCustomers('buyer'),
					getAllCustomers('advisor')
				]);

				const sellers = sellersRes.data || [];
				const buyers = buyersRes.data || [];
				const advisors = advisorsRes.data || [];
				
				const allCustomers = [...sellers, ...buyers, ...advisors];
				const activeCustomers = allCustomers.filter(c => c.active);
				
				// New customers this month
				const oneMonthAgo = new Date();
				oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
				const newCustomersThisMonth = allCustomers.filter(c => {
					const createdAt = new Date(c.createdAt);
					return createdAt >= oneMonthAgo;
				}).length;

				// Projects
				const projectsRes = await getAllProjects();
				const projects = projectsRes.data || [];
				const activeProjects = projects.filter(p => p.active);
				const pendingProjects = projects.filter(p => p.status === 'review' || (!p.validated && p.status === 'draft'));
				const highlightedProjects = projects.filter(p => p.highlighted);

				// Deals
				const dealsRes = await getAllDeals();
				const deals = dealsRes.data || [];
				const activeDeals = deals.filter(d => d.active);
				
				// New deals this month
				const newDealsThisMonth = deals.filter(d => {
					const createdAt = new Date(d.createdAt);
					return createdAt >= oneMonthAgo;
				}).length;

				// Subscriptions
				const subscriptionsRes = await getAllPlanCustomer();
				const subscriptions = subscriptionsRes.data || [];
				const activeSubscriptions = subscriptions.filter(s => {
					if (!s.endDate) return true; // Free plan
					return new Date(s.endDate) > new Date();
				});
				const freePlanSubscriptions = subscriptions.filter(s => {
					return s.plan && s.plan.monthPrice === 0;
				});
				
				// Separar suscripciones activas en Basic y Premium basándose en el título del plan
				const basicSubscriptions = activeSubscriptions.filter(s => {
					if (!s.plan || !s.plan.title) return false;
					const planTitle = getNonEmpty(s.plan.title).toLowerCase();
					return planTitle.includes('basic') || planTitle.includes('basique');
				});
				
				const premiumSubscriptions = activeSubscriptions.filter(s => {
					if (!s.plan || !s.plan.title) return false;
					const planTitle = getNonEmpty(s.plan.title).toLowerCase();
					return planTitle.includes('premium');
				});

				// Recent activity (last 10 items)
				const recentCustomers = allCustomers
					.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
					.slice(0, 5)
					.map(c => ({
						type: 'customer',
						name: c.fullName,
						role: c.role?.[0] || 'customer',
						date: c.createdAt,
						active: c.active
					}));

				const recentProjects = projects
					.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
					.slice(0, 5)
					.map(p => ({
						type: 'project',
						name: p.title?.en || p.title?.es || 'Untitled Project',
						date: p.createdAt,
						status: p.status
					}));

				const recentDeals = deals
					.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
					.slice(0, 5)
					.map(d => ({
						type: 'deal',
						name: `Deal #${d._id?.slice(-6)}`,
						date: d.createdAt,
						active: d.active
					}));

				const activity = [...recentCustomers, ...recentProjects, ...recentDeals]
					.sort((a, b) => new Date(b.date) - new Date(a.date))
					.slice(0, 10);

				setStats({
					admins: admins.data || 0,
					activeUsers: activeUsersCount,
					totalCustomers: allCustomers.length,
					activeCustomers: activeCustomers.length,
					sellers: sellers.length,
					buyers: buyers.length,
					advisors: advisors.length,
					newCustomersThisMonth,
					totalProjects: projects.length,
					activeProjects: activeProjects.length,
					pendingProjects: pendingProjects.length,
					highlightedProjects: highlightedProjects.length,
					totalDeals: deals.length,
					activeDeals: activeDeals.length,
					newDealsThisMonth,
					activeSubscriptions: activeSubscriptions.length,
					freePlanSubscriptions: freePlanSubscriptions.length,
					basicSubscriptions: basicSubscriptions.length,
					premiumSubscriptions: premiumSubscriptions.length,
				});

				setRecentActivity(activity);

				// Calculate customer evolution data after fetching
				calculateCustomerEvolution(sellers, buyers, advisors, evolutionPeriod);
			} catch (err) {
				alertError({ error: err, customMessage: 'Could not get statistics.' });
			} finally {
				setLoading(false);
			}
		}
		fetchData();
	}, [evolutionPeriod]);

	const StatCard = ({ title, value, icon: Icon, onClick, color = 'primary' }) => (
		<div className='col-6 col-md-3 col-lg-2 my-2' role={onClick ? 'button' : undefined} onClick={onClick}>
			<div className='card h-100'>
				<div className='card-body d-flex flex-column justify-content-center'>
					<div className='d-flex align-items-center mb-2'>
						<Icon style={{ fontSize: 24, marginRight: 8, color: color === 'primary' ? '#3699FF' : color === 'success' ? '#1BC5BD' : color === 'warning' ? '#FFA800' : '#F64E60' }} />
						<h6 className='card-title mb-0' style={{ fontSize: '0.875rem', fontWeight: '500' }}>{title}</h6>
					</div>
					<div className='contentDash text-center' style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{loading ? '...' : value}</div>
				</div>
			</div>
		</div>
	);

	const SectionTitle = ({ title }) => (
		<div className='col-12 mb-3 mt-4'>
			<h4 style={{ fontWeight: '600', color: '#3F4254', borderBottom: '2px solid #E4E6EF', paddingBottom: '8px' }}>{title}</h4>
		</div>
	);

	// Chart options for customer distribution
	const customerDistributionOptions = {
		chart: {
			type: 'pie',
		},
		labels: ['Sellers', 'Buyers', 'Advisors'],
		colors: ['#3699FF', '#1BC5BD', '#FFA800'],
		legend: {
			position: 'bottom',
		},
		responsive: [{
			breakpoint: 480,
			options: {
				chart: {
					width: 200
				},
				legend: {
					position: 'bottom'
				}
			}
		}]
	};

	const customerDistributionSeries = [stats.sellers, stats.buyers, stats.advisors];

	// Chart options for project status
	const projectStatusOptions = {
		chart: {
			type: 'bar',
		},
		plotOptions: {
			bar: {
				horizontal: false,
				columnWidth: '55%',
			},
		},
		dataLabels: {
			enabled: false
		},
		xaxis: {
			categories: ['Total', 'Active', 'Pending Review', 'Highlighted'],
		},
		colors: ['#3699FF'],
		fill: {
			type: 'gradient',
			gradient: {
				shade: 'light',
				type: 'vertical',
				shadeIntensity: 0.5,
				gradientToColors: ['#1BC5BD'],
				inverseColors: false,
				opacityFrom: 1,
				opacityTo: 1,
				stops: [0, 100]
			}
		}
	};

	const projectStatusSeries = [{
		name: 'Projects',
		data: [stats.totalProjects, stats.activeProjects, stats.pendingProjects, stats.highlightedProjects]
	}];

	return (
		<>
			{/* Section: Users & Customers Overview */}
			<div className='row'>
				<SectionTitle title="Users & Customers Overview" />
				<StatCard
					title="Administrators"
					value={stats.admins}
					icon={AssignmentIndIcon}
					onClick={() => history.push('/admins')}
					color="primary"
				/>
				<StatCard
					title="Online Users"
					value={stats.activeUsers}
					icon={PeopleAltIcon}
					onClick={() => history.push('/active-users')}
					color="primary"
				/>
				<StatCard
					title="Total Customers"
					value={stats.totalCustomers}
					icon={BusinessIcon}
					color="primary"
				/>
				<StatCard
					title="Active Customers"
					value={stats.activeCustomers}
					icon={CheckCircleIcon}
					color="success"
				/>
				<StatCard
					title="New This Month"
					value={stats.newCustomersThisMonth}
					icon={TrendingUpIcon}
					color="warning"
				/>
			</div>

			{/* Section: Customers by Type */}
			<div className='row'>
				<SectionTitle title="Customers by Type" />
				<StatCard
					title="Sellers"
					value={stats.sellers}
					icon={ShoppingCartIcon}
					onClick={() => history.push('/sellers')}
					color="primary"
				/>
				<StatCard
					title="Buyers"
					value={stats.buyers}
					icon={AccountBalanceIcon}
					onClick={() => history.push('/buyers')}
					color="primary"
				/>
				<StatCard
					title="Advisors"
					value={stats.advisors}
					icon={BusinessIcon}
					onClick={() => history.push('/advisors')}
					color="primary"
				/>
			</div>

			{/* Customer Evolution Chart */}
			<div className='row mb-4'>
				<div className='col-12'>
					<div className='card'>
						<div className='card-header d-flex justify-content-between align-items-center'>
							<h5 className='card-title mb-0'>Customer Registration Evolution</h5>
							<FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
								<InputLabel>Period</InputLabel>
								<Select
									value={evolutionPeriod}
									onChange={(e) => setEvolutionPeriod(e.target.value)}
									label="Period"
								>
									<MenuItem value={7}>Last 7 days</MenuItem>
									<MenuItem value={30}>Last 30 days</MenuItem>
									<MenuItem value={90}>Last 90 days</MenuItem>
									<MenuItem value={180}>Last 6 months</MenuItem>
									<MenuItem value={365}>Last year</MenuItem>
								</Select>
							</FormControl>
						</div>
						<div className='card-body'>
							{loading ? (
								<div className='text-center'>Loading chart data...</div>
							) : (
								<Chart
									options={{
										chart: {
											type: 'line',
											zoom: { enabled: false },
											toolbar: { show: false }
										},
										stroke: {
											curve: 'smooth',
											width: 3
										},
										colors: ['#3699FF', '#1BC5BD', '#FFA800'],
										xaxis: {
											categories: customerEvolutionData.dates,
											labels: {
												rotate: -45,
												rotateAlways: true
											}
										},
										yaxis: {
											title: { text: 'Total Customers' }
										},
										legend: {
											position: 'top'
										},
										tooltip: {
											shared: true,
											intersect: false
										}
									}}
									series={[
										{ name: 'Sellers', data: customerEvolutionData.sellers },
										{ name: 'Buyers', data: customerEvolutionData.buyers },
										{ name: 'Advisors', data: customerEvolutionData.advisors }
									]}
									type="line"
									height={350}
								/>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Section: Projects & Deals */}
			<div className='row'>
				<SectionTitle title="Projects & Deals" />
				<StatCard
					title="Total Projects"
					value={stats.totalProjects}
					icon={FolderIcon}
					onClick={() => history.push('/projects')}
					color="primary"
				/>
				<StatCard
					title="Active Projects"
					value={stats.activeProjects}
					icon={CheckCircleIcon}
					color="success"
				/>
				<StatCard
					title="Pending Review"
					value={stats.pendingProjects}
					icon={HourglassEmptyIcon}
					color="warning"
				/>
				<StatCard
					title="Highlighted"
					value={stats.highlightedProjects}
					icon={StarIcon}
					color="warning"
				/>
				<StatCard
					title="Total Deals"
					value={stats.totalDeals}
					icon={TrendingUpIcon}
					onClick={() => history.push('/deals')}
					color="primary"
				/>
				<StatCard
					title="Active Deals"
					value={stats.activeDeals}
					icon={CheckCircleIcon}
					color="success"
				/>
				<StatCard
					title="New Deals (Month)"
					value={stats.newDealsThisMonth}
					icon={TrendingUpIcon}
					color="warning"
				/>
			</div>

			{/* Section: Subscriptions */}
			<div className='row'>
				<SectionTitle title="Subscriptions" />
				<StatCard
					title="Active Subscriptions"
					value={stats.activeSubscriptions}
					icon={CardGiftcardIcon}
					color="success"
				/>
				<StatCard
					title="Free Plan"
					value={stats.freePlanSubscriptions}
					icon={CardGiftcardIcon}
					color="primary"
				/>
				<StatCard
					title="Basic"
					value={stats.basicSubscriptions}
					icon={StarIcon}
					color="info"
				/>
				<StatCard
					title="Premium"
					value={stats.premiumSubscriptions}
					icon={StarIcon}
					color="warning"
				/>
			</div>

			{/* Charts Row */}
			<div className='row justify-content-center mb-4'>
				<div className='col-12 col-md-6 mb-3'>
					<div className='card'>
						<div className='card-header'>
							<h5 className='card-title'>Customer Distribution</h5>
						</div>
						<div className='card-body'>
							<Chart
								options={customerDistributionOptions}
								series={customerDistributionSeries}
								type="pie"
								height={300}
							/>
						</div>
					</div>
				</div>
				<div className='col-12 col-md-6 mb-3'>
					<div className='card'>
						<div className='card-header'>
							<h5 className='card-title'>Projects Status</h5>
						</div>
						<div className='card-body'>
							<Chart
								options={projectStatusOptions}
								series={projectStatusSeries}
								type="bar"
								height={300}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Recent Activity */}
			<div className='row justify-content-center'>
				<div className='col-12'>
					<div className='card'>
						<div className='card-header'>
							<h5 className='card-title'>Recent Activity</h5>
						</div>
						<div className='card-body'>
							{loading ? (
								<div className='text-center'>Loading...</div>
							) : recentActivity.length === 0 ? (
								<div className='text-center'>No recent activity</div>
							) : (
								<div className='table-responsive'>
									<table className='table table-hover'>
										<thead>
											<tr>
												<th>Type</th>
												<th>Name</th>
												<th>Date</th>
												<th>Status</th>
											</tr>
										</thead>
										<tbody>
											{recentActivity.map((item, index) => (
												<tr key={index}>
													<td>
														<span className={`badge badge-${item.type === 'customer' ? 'primary' : item.type === 'project' ? 'success' : 'info'}`}>
															{item.type}
														</span>
													</td>
													<td>{item.name}</td>
													<td>{new Date(item.date).toLocaleDateString('es-ES')}</td>
													<td>
														<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
															{item.active !== undefined && (
																<span className={`badge badge-${item.active ? 'success' : 'secondary'}`}>
																	{item.active ? 'Active' : 'Inactive'}
																</span>
															)}
															{item.status && (
																<span className={`badge badge-${item.status === 'published' ? 'success' : item.status === 'review' ? 'warning' : 'secondary'}`}>
																	{item.status}
																</span>
															)}
															{item.role && (
																<span className="badge badge-info">{item.role}</span>
															)}
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
